import { AdminCompliance } from './admin/Compliance';
import { Shield } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, NavLink, useParams } from 'react-router';
import { api } from '../lib/api';
import type { AdminAccessRequest } from '../lib/types';
import { useParent } from './ParentContext';
import { AdminPlans } from './Plans';
import { AdminFinances } from './Finances';
import { AdminSupport, type SupportQueue } from './Support';
import { AccessRequests } from './admin/AccessRequests';
import { Audit } from './admin/Audit';
import { Families } from './admin/Families';
import { Growth } from './admin/Growth';
import { AdminPartners } from './admin/Partners';
import { Roles } from './admin/Roles';
import { ServerSettings } from './admin/ServerSettings';

/** Platform admin portal: every family on this server, their feedback, the server's settings, and the audit trail. */
export function AdminPortal() {
  const { tab = 'overview' } = useParams();
  const { toast } = useParent();
  const [support, setSupport] = useState<SupportQueue | null>(null);
  const [requests, setRequests] = useState<AdminAccessRequest[] | null>(null);

  const loadSupport = useCallback(() => {
    api.get<SupportQueue>('/api/admin/support/tickets').then(setSupport).catch((e) => toast({ text: e.message, tone: 'alert' }));
  }, [toast]);
  const loadRequests = useCallback(() => {
    api.get<AdminAccessRequest[]>('/api/admin/access-requests').then(setRequests).catch((e) => toast({ text: e.message, tone: 'alert' }));
  }, [toast]);
  useEffect(loadSupport, [loadSupport]);
  useEffect(loadRequests, [loadRequests]);

  const waiting = support?.tickets.filter((t) => t.needsAnswer).length ?? 0;
  const pendingRequests = requests?.filter((r) => r.status === 'pending').length ?? 0;
  const tabs = [
    { id: 'overview', label: 'Families' },
    { id: 'requests', label: pendingRequests ? `Requests (${pendingRequests})` : 'Requests' },
    { id: 'support', label: waiting ? `Support (${waiting})` : 'Support' },
    { id: 'roles', label: 'Roles' },
    { id: 'plans', label: 'Plans' },
    { id: 'growth', label: 'Growth' },
    { id: 'partners', label: 'Partners' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'finances', label: 'Finances' },
    { id: 'settings', label: 'Server settings' },
    { id: 'audit', label: 'Audit log' },
  ];

  // API access moved into Server settings (#658).
  if (tab === 'api') return <Navigate to="/admin/settings#s-api" replace />;

  return (
    <div className="page admin-page">
      <div className="page-head">
        <div>
          <h1>
            <Shield size={24} className="inline-icon" /> Admin
          </h1>
          <p className="muted">Manage this ClassGuardian server and everyone on it.</p>
        </div>
      </div>
      <nav className="tabs" role="tablist">
        {tabs.map((t) => (
          <NavLink key={t.id} to={`/admin/${t.id}`} className={() => (tab === t.id ? 'active' : '')} role="tab" aria-selected={tab === t.id}>
            {t.label}
          </NavLink>
        ))}
      </nav>
      {tab === 'overview' && <Families />}
      {tab === 'requests' && <AccessRequests items={requests} reload={loadRequests} />}
      {(tab === 'support' || tab === 'feedback') && <AdminSupport queue={support} reload={loadSupport} />}
      {tab === 'roles' && <Roles />}
      {tab === 'plans' && <AdminPlans />}
      {tab === 'growth' && <Growth />}
      {tab === 'partners' && <AdminPartners />}
      {tab === 'compliance' && <AdminCompliance />}
      {tab === 'finances' && <AdminFinances />}
      {tab === 'settings' && <ServerSettings />}
      {tab === 'audit' && <Audit />}
    </div>
  );
}
